import React, { useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { PackingRecommendationsSection } from './components/packing-recommendations/packing-recommendations';
import { PrintedPackingSlips } from './components/printed-packing-slips';
import { ScanPageSettings } from './components/scan-page-settings';
import style from './style.module.less';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { UserSettings } from '../../../../enums/user-settings/user-settings';
import {
  usePackingSlipBarcode,
  usePackingSlipFormat,
  usePackingSlipInventoryLocation,
  usePackingSlipLogos,
  usePackingSlipPrices,
  usePackingSlipShowDiscounts,
  usePackingSlipSkus,
  usePackingSlipSorting,
  usePackingSlipZeroQuantity,
} from '../../../../hooks/use-seller-setting';
import { useRequiredManualConfirmationSetting, useVerifyItemsBySetting } from '../../../../hooks/use-user-setting';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { updateUserSettingsBulk as updateUserSettingsBulkAction } from '../../../../store/user/user.actions';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import { PackingRecommendations } from '../../../../constants/features';
import { updateSellerSettingsBulk, updateUserSettingsBulk } from '../../api/save-settings';
import SaveBar from '../../components/save-bar';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';
import messages from '../../messages';
import settingsPageStyle from '../../settings-page.module.less';

export const WarehousePacking = () => {
  const dispatch = useDispatch();
  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);

  const currentPackingSlipFormat = usePackingSlipFormat();
  const currentPackingSlipSorting = usePackingSlipSorting();
  const currentPackingSlipLogos = usePackingSlipLogos();
  const currentPackingSlipPrices = usePackingSlipPrices();
  const currentPackingSlipSkus = usePackingSlipSkus();
  const currentPackingSlipBarcode = usePackingSlipBarcode();
  const currentPackingSlipInventoryLocation = usePackingSlipInventoryLocation();
  const currentPackingSlipShowDiscounts = usePackingSlipShowDiscounts();
  const currentPackingSlipZeroQuantity = usePackingSlipZeroQuantity();

  const currentVerifyItemsBy = useVerifyItemsBySetting();
  const currentRequiredManualConfirmation = useRequiredManualConfirmationSetting();

  const originalData = {
    packingSlipFormat: currentPackingSlipFormat,
    packingSlipOrderItemsBy: currentPackingSlipSorting,
    packingSlipIncludeStoreLogo: currentPackingSlipLogos,
    packingSlipIncludeItemPrice: currentPackingSlipPrices,
    packingSlipIncludeItemSku: currentPackingSlipSkus,
    packingSlipIncludeScanToViewBarcode: currentPackingSlipBarcode,
    packingSlipIncludeWarehouseLocation: currentPackingSlipInventoryLocation,
    packingSlipShowDiscountsAsLineItems: currentPackingSlipShowDiscounts,
    packingSlipHideZeroQuantityItems: currentPackingSlipZeroQuantity,
    verifyItemsBy: currentVerifyItemsBy,
    requiredManualConfirmation: currentRequiredManualConfirmation,
  };

  const [formData, setFormData] = useState(originalData);

  const hasChanges = !isEqual(formData, originalData);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRequiredManualConfirmationChange = (value) => {
    setFormData((prev) => ({ ...prev, requiredManualConfirmation: value }));
  };

  const handleVerifyItemsByChange = (value) => {
    setFormData((prev) => ({ ...prev, verifyItemsBy: value }));
  };

  const handleSave = async () => {
    const sellerSettingsToSave = {
      [SellerSettings.packingSlipFormat.key]: formData.packingSlipFormat,
      [SellerSettings.packingSlipSorting.key]: formData.packingSlipOrderItemsBy,
      [SellerSettings.packingSlipLogos.key]: formData.packingSlipIncludeStoreLogo,
      [SellerSettings.packingSlipPrices.key]: formData.packingSlipIncludeItemPrice,
      [SellerSettings.packingSlipSkus.key]: formData.packingSlipIncludeItemSku,
      [SellerSettings.packingSlipBarcode.key]: formData.packingSlipIncludeScanToViewBarcode,
      [SellerSettings.packingSlipInventoryLocation.key]: formData.packingSlipIncludeWarehouseLocation,
      [SellerSettings.packingSlipShowDiscounts.key]: formData.packingSlipShowDiscountsAsLineItems,
      [SellerSettings.packingSlipZeroQuantity.key]: formData.packingSlipHideZeroQuantityItems,
    };

    const userSettingsToSave = {
      [UserSettings.verifyItemsBy.settingName]: formData.verifyItemsBy,
      [UserSettings.requiredManualConfirmation.settingName]: formData.requiredManualConfirmation,
    };

    const [newSellerSettings, newUserSettings] = await Promise.all([
      updateSellerSettingsBulk(sellerSettingsToSave),
      updateUserSettingsBulk(userSettingsToSave),
    ]);

    dispatch(saveNewSellerSettings(newSellerSettings));
    dispatch(updateUserSettingsBulkAction(newUserSettings));
  };

  return (
    <>
      <div className={settingsPageStyle.content}>
        <SettingsPageTitle value={messages.warehousePacking} />
        <FlexColumn className={style.elements} gap={Gap.Space24}>
          {isPackingRecommendationsEnabled && <PackingRecommendationsSection />}
          <ScanPageSettings
            onRequiredManualConfirmationChange={handleRequiredManualConfirmationChange}
            onVerifyItemsByChange={handleVerifyItemsByChange}
            requiredManualConfirmation={formData.requiredManualConfirmation}
            verifyItemsBy={formData.verifyItemsBy}
          />
          <PrintedPackingSlips onChange={handleFieldChange} values={formData} />
        </FlexColumn>
      </div>
      <SaveBar className={style['footer-container']} disabled={!hasChanges} onSave={handleSave} />
    </>
  );
};
